import http from "./http"; // or wherever your axios/fetch wrapper is
import { Gebruikersfout, tekstVoorServercode } from "./utils/foutmelding";
import { PdfOpenenVia } from "./enums";

type DownloadUrlResponse = {
  url: string;
  /** When set, use this (not `url`) for ms-word / ms-excel / ms-powerpoint URI schemes — Graph `webUrl` is often a `/:w:/` link Word rejects. */
  webDavUrl?: string;
};

export type UploadProgress = {
  uploadedBytes: number;
  totalBytes: number;
  percentage: number;
};

export type UploadProgressCallback = (progress: UploadProgress) => void;

function extractServerMessage(err: any, fallback = "Request failed"): string {
  // axios-style error
  const res = err?.response;
  if (res) {
    const status = res.status;
    const statusText = res.statusText || "";
    const data = res.data;
    let detail: string | undefined;
    if (data?.message) {
      if (Array.isArray(data.message)) detail = data.message.join(", ");
      else detail = String(data.message);
    } else if (typeof data === "string") {
      detail = data;
    } else if (data?.error) {
      detail = String(data.error);
    } else if (data?.code) {
      detail = String(data.code);
    }
    const prefix = status
      ? `${status}${statusText ? " " + statusText : ""}`
      : "Error";
    return detail ? `${prefix}: ${detail}` : `${prefix}`;
  }
  // generic
  return err?.message || fallback;
}

/** Mirrors Prisma `DocumentCategory` (leaf values; UI groups live in `DOCUMENT_CATEGORY_OPTIONS_GROUPED`). */
export type DocumentCategory =
  | "AANBIEDINGSBRIEF_EN_DOSSIER"
  | "AANTEKENINGEN"
  | "AANVRAAG_RADIOLOGIE"
  | "AANVRAAG_MRI"
  | "VOORBEREIDINGSFORMULIER"
  | "TOESTEMMINGSFORMULIER"
  | "BEELDVORMING_DVD"
  | "OPGEVRAAGDE_INFORMATIE"
  | "NAGESTUURDE_INFORMATIE"
  | "RAPPORT_WORD"
  | "RAPPORT_PDF"
  | "BEPERKINGENLIJST"
  | "AMA_BEREKENING"
  | "ELINK_METING"
  | "ELINK_DATA"
  | "VERKLARING_DIGITAAL_VERSTUREN"
  | "ID_BETROKKENE"
  | "MEDISCHE_FOTOGRAFIE"
  | "AKKOORD_BETROKKENE"
  | "REACTIE_BETROKKENE"
  | "ANTWOORD_REACTIE_BETROKKENE"
  | "AKKOORD_OPDRACHTGEVER"
  | "AANVULLENDE_VRAGEN_OPDRACHTGEVER"
  | "ANTWOORD_AANVULLENDE_VRAGEN_OPDRACHTGEVER"
  | "FACTUUR"
  | "OVERIG";

export const DOCUMENT_CATEGORY_OPTIONS_GROUPED: {
  groupLabel: string;
  options: { value: DocumentCategory; label: string }[];
}[] = [
  {
    groupLabel: "Aanbiedingsbrief en dossier",
    options: [
      {
        value: "AANBIEDINGSBRIEF_EN_DOSSIER",
        label: "Aanbiedingsbrief & dossier",
      },
    ],
  },
  {
    groupLabel: "Aantekeningen en radiologie-aanvraag",
    options: [
      { value: "AANTEKENINGEN", label: "Aantekeningen" },
      { value: "AANVRAAG_RADIOLOGIE", label: "Aanvraag radiologie" },
      { value: "AANVRAAG_MRI", label: "Aanvraag MRI" },
    ],
  },
  {
    groupLabel: "Formulieren",
    options: [
      { value: "VOORBEREIDINGSFORMULIER", label: "Voorbereidingsformulier" },
      { value: "TOESTEMMINGSFORMULIER", label: "Toestemmingsformulier" },
    ],
  },
  {
    groupLabel: "Beeldvorming",
    options: [{ value: "BEELDVORMING_DVD", label: "DVD" }],
  },
  {
    groupLabel: "Verslaglegging",
    options: [
      { value: "OPGEVRAAGDE_INFORMATIE", label: "Opgevraagde informatie" },
      { value: "NAGESTUURDE_INFORMATIE", label: "Nagestuurde informatie" },
    ],
  },
  {
    groupLabel: "Rapport",
    options: [
      { value: "RAPPORT_WORD", label: "Rapport Word" },
      { value: "RAPPORT_PDF", label: "Rapport PDF" },
      { value: "BEPERKINGENLIJST", label: "Beperkingenlijst" },
      { value: "AMA_BEREKENING", label: "AMA-berekening" },
      { value: "ELINK_METING", label: "E-link meting" },
      { value: "ELINK_DATA", label: "E-link data" },
      {
        value: "VERKLARING_DIGITAAL_VERSTUREN",
        label: "Verklaring digitaal versturen",
      },
      { value: "ID_BETROKKENE", label: "ID betrokkene" },
      { value: "MEDISCHE_FOTOGRAFIE", label: "Medische fotografie" },
      { value: "AKKOORD_BETROKKENE", label: "Akkoord betrokkene" },
      { value: "REACTIE_BETROKKENE", label: "Reactie betrokkene" },
      {
        value: "ANTWOORD_REACTIE_BETROKKENE",
        label: "Antwoord reactie betrokkene",
      },
      { value: "AKKOORD_OPDRACHTGEVER", label: "Akkoord opdrachtgever" },
      {
        value: "AANVULLENDE_VRAGEN_OPDRACHTGEVER",
        label: "Aanvullende vragen opdrachtgever",
      },
      {
        value: "ANTWOORD_AANVULLENDE_VRAGEN_OPDRACHTGEVER",
        label: "Antwoord aanvullende vragen opdrachtgever",
      },
    ],
  },
  {
    groupLabel: "Facturatie",
    options: [{ value: "FACTUUR", label: "Factuur" }],
  },
  {
    groupLabel: "Overig",
    options: [{ value: "OVERIG", label: "Overig" }],
  },
];

export const DOCUMENT_CATEGORY_LABELS: Record<DocumentCategory, string> =
  DOCUMENT_CATEGORY_OPTIONS_GROUPED.reduce(
    (acc, g) => {
      for (const o of g.options) {
        acc[o.value] = o.label;
      }
      return acc;
    },
    {} as Record<DocumentCategory, string>,
  );

export type DocFileRow = {
  id: string;
  naam: string;
  contentType?: string | null;
  category?: DocumentCategory | null;
  aangemaaktOp?: string | null;
  laatstBewerktOp?: string | null;
  laatstBewerktDoor?: string | null;
  /** eTag van SharePoint; ontbreekt bij een net geüpload bestand. */
  etag?: string | null;
  /** Grootte in bytes volgens SharePoint; ontbreekt bij een net geüpload bestand. */
  grootte?: number | null;
};

export type DocFolderRow = { name: string; path: string };

export type ListDocumentsResponse = {
  folders: DocFolderRow[];
  files: DocFileRow[];
  // Alleen in de dev-omgeving: dossiermap bestaat niet in de DEV-library.
  devMapOntbreekt?: boolean;
  /** De dossiermap wacht op verhuizing (Actief ↔ Archief) omdat er een bestand open staat. */
  mapVerhuizingWacht?: boolean;
  /** D3: gearchiveerd dossier en geen rol die mag wijzigen. */
  alleenLezen?: boolean;
  /** §4B: knop "Toon in OneDrive" (vlag aan en dossier actief). */
  toonInOneDriveKnop?: boolean;
};

export async function listDocuments(
  dossierKenmerk: string,
  folder?: string,
  cacheBust?: boolean,
): Promise<ListDocumentsResponse> {
  const params: Record<string, string> = folder ? { folder } : {};
  if (cacheBust) params._t = String(Date.now());
  const { data } = await http.get<ListDocumentsResponse>(
    `/records/${dossierKenmerk}/documents`,
    { params },
  );
  return data;
}

/** Voeg pdf's samen tot één nieuwe pdf; de volgorde van documentIds is de paginavolgorde. */
export async function samenvoegenTotPdf(
  dossierKenmerk: string,
  documentIds: string[],
  naam: string,
  folderPath?: string,
): Promise<{ id: string; naam: string }> {
  const { data } = await http.post<{ id: string; naam: string }>(
    `/records/${dossierKenmerk}/documents/samenvoegen`,
    { documentIds, naam, ...(folderPath ? { folderPath } : {}) },
  );
  return data;
}

export type UploadUrlAntwoord =
  | {
      bestaatAl?: undefined;
      blobName: string;
      uploadUrl: string;
      headers: Record<string, string>;
    }
  | {
      bestaatAl: true;
      /** Ontbreekt voor wie de Beeldvorming niet ziet (D2, L11). */
      documentId?: string;
      blobName: string;
    };

export async function vraagUploadUrl(
  dossierKenmerk: string,
  verzoek: {
    filename: string;
    mimeType: string;
    size: number;
    category?: DocumentCategory;
    folderPath?: string;
    batchId?: string;
  },
): Promise<UploadUrlAntwoord> {
  const { data } = await http.post<UploadUrlAntwoord>(
    `/records/${dossierKenmerk}/documents/upload-url`,
    verzoek,
  );
  return data;
}

export async function legUploadVast(
  dossierKenmerk: string,
  verzoek: {
    blobName: string;
    originalFilename: string;
    mimeType: string;
    size: number;
    category?: DocumentCategory;
    /** Het item-id uit het laatste antwoord van de uploadsessie (A2-9). */
    itemId?: string;
  },
): Promise<{ id: string }> {
  const { data } = await http.post<{ id: string }>(
    `/records/${dossierKenmerk}/documents/commit`,
    verzoek,
  );
  return data;
}

/**
 * Splits bestanden in leesbaar en onleesbaar vóór ze de wachtrij in gaan.
 * Online-only Dropbox-/OneDrive-bestanden hebben in de browser wél een naam
 * en grootte, maar het lezen van de bytes faalt (NotReadableError) — dat zou
 * anders pas halverwege de upload opduiken. Eén byte lezen is genoeg; lege
 * bestanden (0 bytes) tellen ook als onleesbaar, want de upload-sessie
 * accepteert die niet. Volgorde blijft behouden.
 */
export async function probeerLeesbaarheid<T extends { file: File }>(
  items: T[],
): Promise<{ leesbaar: T[]; onleesbaar: T[] }> {
  const leesbaar: T[] = [];
  const onleesbaar: T[] = [];
  const STAP = 20;
  for (let i = 0; i < items.length; i += STAP) {
    const uitkomsten = await Promise.all(
      items.slice(i, i + STAP).map(async (item) => {
        if (item.file.size === 0) return false;
        try {
          await item.file.slice(0, 1).arrayBuffer();
          return true;
        } catch {
          return false;
        }
      }),
    );
    uitkomsten.forEach((ok, j) =>
      (ok ? leesbaar : onleesbaar).push(items[i + j]),
    );
  }
  return { leesbaar, onleesbaar };
}

export async function renameDocument(
  docId: string,
  naam: string,
): Promise<void> {
  await http.patch(`/documents/${docId}`, { naam: naam.trim() });
}

export type NaamWaarschuwing = {
  naam: string;
  reden: "ANDER_KENMERK" | "ANDERE_PATIENT" | "ZELFDE_ACHTERNAAM";
  /** Het stuk van de bestandsnaam dat de treffer gaf. */
  fragment: string;
};

/**
 * Voor de upload (I17-2): lijken bestandsnamen bij een andere patient te
 * horen? Een storing blokkeert niets: dan komt er gewoon geen waarschuwing.
 */
export async function controleerBestandsnamen(
  dossierKenmerk: string,
  namen: string[],
): Promise<NaamWaarschuwing[]> {
  if (namen.length === 0) return [];
  try {
    const { data } = await http.post<{ waarschuwingen: NaamWaarschuwing[] }>(
      `/records/${dossierKenmerk}/documents/naamcontrole`,
      { namen: namen.slice(0, 500) },
    );
    return data?.waarschuwingen ?? [];
  } catch {
    return [];
  }
}

/** Categorie van een bestaand document wijzigen (A4): dezelfde PATCH als hernoemen. */
export async function wijzigDocumentCategorie(
  docId: string,
  category: DocumentCategory,
): Promise<void> {
  try {
    await http.patch(`/documents/${docId}`, { category });
  } catch (err: unknown) {
    throw new Error(serverTekst(err) ?? "Categorie wijzigen is niet gelukt.", {
      cause: err,
    });
  }
}

/** Maakt een kopie van een document in dezelfde map (A3) en geeft de nieuwe rij terug. */
export async function dupliceerDocument(
  docId: string,
): Promise<{ id: string; naam: string; url: string }> {
  try {
    const { data } = await http.post<{ id: string; naam: string; url: string }>(
      `/documents/${docId}/dupliceer`,
    );
    return data;
  } catch (err: unknown) {
    throw new Error(serverTekst(err) ?? "Dupliceren is niet gelukt.", {
      cause: err,
    });
  }
}

/** Word-soorten die Microsoft naar pdf kan omzetten (zelfde lijst als de server). */
export const isWordBestand = (naam: string) =>
  /\.(docx?|dotx?|dotm|odt|rtf)$/i.test(naam);

/** Naam van de pdf-kopie: dezelfde naam met .pdf. */
export const pdfNaamVoor = (naam: string) => naam.replace(/\.[^.]+$/, ".pdf");

/** Zet een pdf-kopie van een Word-bestand in dezelfde map (trein 68). */
export async function maakPdfKopie(
  docId: string,
): Promise<{ id: string; naam: string }> {
  try {
    const { data } = await http.post<{ id: string; naam: string }>(
      `/documents/${docId}/pdf-kopie`,
    );
    return data;
  } catch (err: unknown) {
    throw new Error(serverTekst(err) ?? "De pdf-kopie is niet gelukt.", {
      cause: err,
    });
  }
}

export async function createFolder(
  dossierKenmerk: string,
  folderName: string,
  folderPath?: string,
): Promise<{ path: string }> {
  const { data } = await http.post<{ path: string }>(
    `/records/${dossierKenmerk}/documents/folders`,
    { folderName: folderName.trim(), folderPath: folderPath || undefined },
  );
  return data;
}

/** De vaste mappen direct onder een dossier; die mogen nooit weg (I5). */
const VASTE_MAPPEN = [
  "aanbiedingsbrief_en_dossier",
  "aantekeningen_en_radiologie",
  "beeldvorming",
  "formulieren",
  "overig",
  "rapport",
  "verslaglegging",
];
const FACTURENMAPPEN = ["00.facturen", "_facturen"];

/**
 * True als de webapp 'Verwijderen' bij deze map aanbiedt. De server beslist;
 * dit houdt alleen de keuze uit het menu bij mappen die nooit weg mogen.
 */
export function kanMapVerwijderen(pad: string): boolean {
  const segmenten = pad.toLowerCase().split("/").filter(Boolean);
  if (segmenten.some((s) => FACTURENMAPPEN.includes(s))) return false;
  return !(segmenten.length === 1 && VASTE_MAPPEN.includes(segmenten[0]));
}

export type MapInhoud = {
  bestanden: number;
  mappen: number;
  magVerwijderen: boolean;
};

/** Telt op de server wat er meegaat als de map wordt verwijderd (I5). */
export async function telMapInhoud(
  dossierKenmerk: string,
  folderPath: string,
): Promise<MapInhoud> {
  try {
    return (
      await http.get<MapInhoud>(
        `/records/${dossierKenmerk}/documents/folders/inhoud`,
        { params: { folderPath } },
      )
    ).data;
  } catch (err: unknown) {
    throw new Error(serverTekst(err) ?? "De map kan niet worden geteld.", {
      cause: err,
    });
  }
}

/**
 * Verwijdert de map met alles erin. De aantallen uit het venster gaan mee: de
 * server weigert als de inhoud intussen is gewijzigd.
 */
export async function verwijderMap(
  dossierKenmerk: string,
  folderPath: string,
  bestanden: number,
  mappen: number,
): Promise<void> {
  try {
    await http.delete(`/records/${dossierKenmerk}/documents/folders`, {
      params: { folderPath, bestanden, mappen },
    });
  } catch (err: unknown) {
    throw new Error(serverTekst(err) ?? "Map verwijderen is niet gelukt.", {
      cause: err,
    });
  }
}

export async function moveDocument(
  docId: string,
  targetFolder: string,
): Promise<{ id: string; url: string }> {
  const { data } = await http.post<{ id: string; url: string }>(
    `/documents/${docId}/move`,
    { targetFolder: targetFolder.trim() },
  );
  return data;
}

export async function deleteDocument(docId: string): Promise<void> {
  await http.delete(`/documents/${docId}`);
}

/** Eén template binnen een bron-groep. */
export type TemplateBron = {
  /** SharePoint-item-id (basis van de categorie-tag). */
  id: string;
  /** Drive-relatief pad (server-gevalideerd bij toevoegen). */
  path: string;
  /** Ruwe bestandsnaam incl. eventueel leidend "xxx - " deel. */
  naam: string;
  /** Naam zonder leidend "xxx - " deel, voor weergave. */
  displayNaam: string;
  /** Categorie-tag (in de kennisbank toegekend), of null. */
  categorie: DocumentCategory | null;
};

/** Eén bron met templates: een arts op het dossier, "Algemeen" of "Combi". */
export type TemplateBronGroep = {
  bron: string;
  templates: TemplateBron[];
};

export type TemplateBronnenResponse = {
  groepen: TemplateBronGroep[];
};

/** Haal de selecteerbare templates op voor "Toevoegen uit templates". */
export async function listTemplateBronnen(
  dossierKenmerk: string,
): Promise<TemplateBronnenResponse> {
  const { data } = await http.get<TemplateBronnenResponse>(
    `/records/${dossierKenmerk}/documents/template-bronnen`,
  );
  return data;
}

/** Resultaat van "Toevoegen uit templates": toegevoegd + (per bestand) mislukt. */
export type AddFromTemplatesResult = {
  created: { id: string; naam: string; url: string }[];
  mislukt: { naam: string; reden: string }[];
};

/** Kopieer geselecteerde templates naar het dossier (server bepaalt de naam). */
export async function addDocumentsFromTemplates(
  dossierKenmerk: string,
  paths: string[],
): Promise<AddFromTemplatesResult> {
  const { data } = await http.post<AddFromTemplatesResult>(
    `/records/${dossierKenmerk}/documents/from-templates`,
    { paths },
  );
  return data;
}

// Microsoft Graph upload sessions require ordered (sequential) chunk uploads.
// Chunk size must be a multiple of 320 KiB for Graph upload sessions.
const GRAPH_CHUNK_UNIT = 320 * 1024; // 320 KiB
const CHUNK_SIZE_SMALL = 10 * 1024 * 1024; // 10MB
const CHUNK_SIZE_MEDIUM = 20 * 1024 * 1024; // 20MB
const CHUNK_SIZE_LARGE = 40 * 1024 * 1024; // 40MB
const FILE_SIZE_MEDIUM_THRESHOLD = 300 * 1024 * 1024; // 300MB
const FILE_SIZE_LARGE_THRESHOLD = 700 * 1024 * 1024; // 700MB
const MAX_CHUNK_RETRIES = 3;
const RETRY_BASE_DELAY_MS = 300;
/**
 * Een PUT die nooit antwoordt (laptop in slaap, VPN-wissel) hield anders de
 * hele uploadwachtrij van álle dossiers vast. Na de timeout gooit fetch een
 * AbortError → retry, en na de laatste poging faalt dit bestand en gaat de
 * wachtrij verder. 40 MB op een trage lijn past ruim binnen vijf minuten.
 */
const CHUNK_TIMEOUT_MS = 5 * 60 * 1000;

function chunkTimeoutSignal(): AbortSignal | undefined {
  // Niet in elke test-/browseromgeving beschikbaar; dan gewoon zonder.
  return typeof AbortSignal !== "undefined" &&
    typeof AbortSignal.timeout === "function"
    ? AbortSignal.timeout(CHUNK_TIMEOUT_MS)
    : undefined;
}

function isGraphUploadSessionUrl(uploadUrl: string): boolean {
  return /graph\.microsoft\.com|up\.1drv\.com|sharepoint\.com/i.test(uploadUrl);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function shouldRetryStatus(status: number): boolean {
  return status === 408 || status === 429 || status >= 500;
}

function roundDownToGraphChunkUnit(size: number): number {
  return Math.max(
    GRAPH_CHUNK_UNIT,
    Math.floor(size / GRAPH_CHUNK_UNIT) * GRAPH_CHUNK_UNIT,
  );
}

function getAdaptiveChunkSize(
  totalSize: number,
  isGraphSession: boolean,
): number {
  let preferred = CHUNK_SIZE_SMALL;

  if (totalSize >= FILE_SIZE_LARGE_THRESHOLD) {
    preferred = CHUNK_SIZE_LARGE;
  } else if (totalSize >= FILE_SIZE_MEDIUM_THRESHOLD) {
    preferred = CHUNK_SIZE_MEDIUM;
  }

  if (isGraphSession) {
    return roundDownToGraphChunkUnit(preferred);
  }

  return preferred;
}

async function uploadChunkWithRetry(
  uploadUrl: string,
  sessionHeaders: Record<string, string>,
  chunk: Blob,
  contentRange: string,
): Promise<Response> {
  let attempt = 0;

  while (true) {
    try {
      const resp = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          ...sessionHeaders,
          "Content-Length": `${chunk.size}`,
          "Content-Range": contentRange,
        },
        body: chunk,
        signal: chunkTimeoutSignal(),
      });

      if (shouldRetryStatus(resp.status) && attempt < MAX_CHUNK_RETRIES) {
        attempt += 1;
        const retryAfterHeader = resp.headers.get("retry-after");
        const retryAfterSeconds = retryAfterHeader
          ? Number.parseInt(retryAfterHeader, 10)
          : Number.NaN;
        const retryDelay = Number.isFinite(retryAfterSeconds)
          ? retryAfterSeconds * 1000
          : RETRY_BASE_DELAY_MS * 2 ** (attempt - 1);
        await delay(retryDelay);
        continue;
      }

      return resp;
    } catch (err) {
      if (attempt >= MAX_CHUNK_RETRIES) throw err;
      attempt += 1;
      await delay(RETRY_BASE_DELAY_MS * 2 ** (attempt - 1));
    }
  }
}

/**
 * Het id van het SharePoint-item uit het laatste antwoord (200/201) van een
 * uploadsessie, of null als dat antwoord niet te lezen is (A2-9). Bij een
 * bezette naam zet SharePoint de upload onder een andere naam; met dit id legt
 * de commit precies dit item vast, niet wat er op het gevraagde pad staat.
 */
async function itemIdUitAntwoord(resp: Response): Promise<string | null> {
  try {
    const data = (await resp.json()) as { id?: unknown };
    return typeof data?.id === "string" && data.id ? data.id : null;
  } catch {
    return null;
  }
}

/** Geeft het item-id uit het laatste antwoord van de sessie, of null (A2-9). */
export async function uploadViaSession(
  uploadUrl: string,
  sessionHeaders: Record<string, string>,
  file: File,
  onProgress?: UploadProgressCallback,
  /** Eerste byte die Graph nog verwacht; > 0 bij een hervatte sessie (I18, WP3). */
  vanaf = 0,
): Promise<string | null> {
  const totalSize = file.size;

  // Current backend returns Microsoft Graph upload sessions. Graph requires
  // chunks to be uploaded sequentially and in order.
  const isGraphSession = isGraphUploadSessionUrl(uploadUrl);
  if (!isGraphSession) {
    console.warn(
      "Non-Graph upload session detected; using sequential chunk upload.",
    );
  }

  const chunkSize = getAdaptiveChunkSize(totalSize, isGraphSession);

  let offset = vanaf;

  onProgress?.({
    uploadedBytes: offset,
    totalBytes: totalSize,
    percentage: totalSize > 0 ? Math.round((offset / totalSize) * 100) : 100,
  });

  while (offset < totalSize) {
    const chunkEnd = Math.min(offset + chunkSize, totalSize);
    const chunk = file.slice(offset, chunkEnd);
    const contentRange = `bytes ${offset}-${chunkEnd - 1}/${totalSize}`;

    const resp = await uploadChunkWithRetry(
      uploadUrl,
      sessionHeaders,
      chunk,
      contentRange,
    );

    if (resp.status === 200 || resp.status === 201) {
      onProgress?.({
        uploadedBytes: chunkEnd,
        totalBytes: totalSize,
        percentage: 100,
      });
      return itemIdUitAntwoord(resp); // upload complete
    }

    if (resp.status === 202) {
      // Partial progress; Graph returns next expected range, continue with next chunk
      offset = chunkEnd;
      onProgress?.({
        uploadedBytes: chunkEnd,
        totalBytes: totalSize,
        percentage: Math.min(100, Math.round((chunkEnd / totalSize) * 100)),
      });
      continue;
    }

    const detail = await resp.text();
    throw new Error(`Storage upload failed: ${resp.status} ${detail}`);
  }

  throw new Error("Storage upload incomplete");
}

const CD_VERANDERD =
  "De cd-map is tijdens het uploaden veranderd; de upload is gestopt. Kies de map opnieuw als hij compleet is.";

/**
 * Uploadt een stroom met vooraf bekende lengte (de cd-zip, D1) in stukken van
 * n × 320 KiB. Met `vanaf` > 0 gaat een onderbroken upload verder: de bytes
 * daarvóór worden gelezen en weggegooid (de zip is deterministisch). Klopt de
 * lengte niet met de planning, dan stopt hij vóór het laatste stuk. Geeft
 * het item-id uit het laatste antwoord, of null (A2-9).
 */
export async function uploadStroomViaSession(
  uploadUrl: string,
  sessionHeaders: Record<string, string>,
  stroom: ReadableStream<Uint8Array>,
  totaal: number,
  vanaf: number,
  onProgress?: UploadProgressCallback,
  chunkGrootte = getAdaptiveChunkSize(totaal, true),
): Promise<string | null> {
  const lezer = stroom.getReader();
  const buffer = new Uint8Array(chunkGrootte);
  let gevuld = 0;
  let gelezen = 0;
  let offset = vanaf;
  let klaar = false;
  let itemId: string | null = null;
  const meld = () =>
    onProgress?.({
      uploadedBytes: offset,
      totalBytes: totaal,
      percentage: totaal > 0 ? Math.round((offset / totaal) * 100) : 100,
    });
  const verstuur = async () => {
    const einde = offset + gevuld;
    const resp = await uploadChunkWithRetry(
      uploadUrl,
      sessionHeaders,
      new Blob([buffer.slice(0, gevuld)]),
      `bytes ${offset}-${einde - 1}/${totaal}`,
    );
    if (resp.status !== 200 && resp.status !== 201 && resp.status !== 202) {
      throw new Error(
        `Storage upload failed: ${resp.status} ${await resp.text()}`,
      );
    }
    klaar = resp.status !== 202;
    if (klaar) itemId = await itemIdUitAntwoord(resp);
    offset = einde;
    gevuld = 0;
    meld();
  };
  try {
    meld();
    for (;;) {
      const { value, done } = await lezer.read();
      if (done) break;
      const overslaan = Math.min(value.length, Math.max(0, vanaf - gelezen));
      gelezen += value.length;
      let stuk = value.subarray(overslaan);
      while (stuk.length > 0) {
        const n = Math.min(stuk.length, chunkGrootte - gevuld);
        buffer.set(stuk.subarray(0, n), gevuld);
        gevuld += n;
        stuk = stuk.subarray(n);
        if (offset + gevuld > totaal) throw new Gebruikersfout(CD_VERANDERD);
        if (gevuld === chunkGrootte) await verstuur();
      }
    }
    if (offset + gevuld !== totaal) throw new Gebruikersfout(CD_VERANDERD);
    if (gevuld > 0) await verstuur();
    if (!klaar) throw new Gebruikersfout(CD_VERANDERD);
  } catch (err) {
    await lezer.cancel().catch(() => undefined);
    throw err;
  }
  return itemId;
}

/** Waar staat een Graph-uploadsessie? De eerste byte die Graph nog verwacht, of null als de sessie weg is. */
export async function vraagSessieStand(
  uploadUrl: string,
): Promise<number | null> {
  let resp: Response;
  try {
    resp = await fetch(uploadUrl, { method: "GET" });
  } catch (err) {
    // De browser kan de sessie niet lezen (CORS of netwerk): stand onbekend.
    // null laat de aanroeper eerst vastleggen proberen en anders een nieuwe
    // sessie beginnen, in plaats van het bestand eeuwig BEZIG te laten (A2-6).
    if (err instanceof TypeError) return null;
    throw err;
  }
  if (resp.status === 404) return null;
  if (!resp.ok) throw new Error(`Uploadsessie opvragen mislukt: ${resp.status}`);
  const data = (await resp.json()) as { nextExpectedRanges?: string[] };
  const start = Number.parseInt(
    (data.nextExpectedRanges?.[0] ?? "").split("-")[0],
    10,
  );
  return Number.isFinite(start) ? start : null;
}

export async function openDocument(
  docId: string,
  filename?: string,
  opties: { alleenLezen?: boolean } = {},
) {
  const route = kiesOpenRoute(filename ?? "", {
    telefoonOfTablet: opTelefoonOfTablet(),
    alleenLezen: opties.alleenLezen === true,
  });
  if (route === "download") {
    await downloadDocumentFile(docId, filename);
    return;
  }
  if (route === "inline") {
    await openPdfInDeBrowser(docId, filename);
    return;
  }
  // Office op de computer start via ms-…:ofe zonder weg te navigeren: geen
  // placeholder (die zou alleen flitsen). Al het andere opent meteen, binnen
  // de klik, een placeholder tegen de pop-upblokkering.
  const viaApp = route === "office-app";
  const win = viaApp ? null : window.open("", "_blank");
  if (win) schrijfPlaatshouder(win, filename);

  let url: string;
  let webDavUrl: string | undefined;
  try {
    ({ url, webDavUrl } = (
      await http.get<DownloadUrlResponse>(`/documents/${docId}/download`)
    ).data);
  } catch (err: unknown) {
    win?.close();
    throw new Error(
      `Document link request failed: ${extractServerMessage(err)}`,
      { cause: err },
    );
  }

  // Office-protocollen weigeren veel Graph-`webUrl`-vormen; de WebDAV-URL werkt.
  const officeDocumentTarget = webDavUrl?.trim() || null;
  const desktopUrl =
    viaApp && filename != null && officeDocumentTarget != null
      ? getDesktopAppUrl(officeDocumentTarget, filename)
      : null;

  if (desktopUrl) {
    window.location.href = desktopUrl;
  } else if (win) {
    win.location.href = url;
  } else {
    window.open(url, "_blank", "noopener");
  }
}

/**
 * Een pdf alleen-lezen in de browser, via het eigen adres van de webapp met
 * een token (D3, D8, D9). Geen SharePoint-adres: daar heeft na de
 * Leden-omschakeling niet iedereen recht op. Het venster opent binnen de klik.
 */
async function openPdfInDeBrowser(docId: string, naam?: string): Promise<void> {
  const win = window.open("", "_blank");
  if (win) schrijfPlaatshouder(win, naam);
  let url: string;
  try {
    url = await inlineWeergaveUrl(await mintDownloadToken(docId, "openen"));
  } catch (err: unknown) {
    win?.close();
    throw new Error(`Openen is niet gelukt: ${extractServerMessage(err)}`, {
      cause: err,
    });
  }
  if (win) win.location.href = url;
  else window.open(url, "_blank", "noopener");
}

/** Adres voor de weergave in de browser; het token vervangt de cookie (I11). */
export async function inlineWeergaveUrl(token: string): Promise<string> {
  const pad = `/documents/file-dl?token=${encodeURIComponent(token)}&weergave=inline`;
  if (await downloadKanViaEigenAdres()) return `${window.location.origin}/api${pad}`;
  const base = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");
  return `${base}${pad}`;
}

/**
 * Het venster dat alvast opent (tegen de pop-upblokkering) was een leeg wit
 * vlak tot het adres er was (I9). Nu staat erin wat er gebeurt. Opent het
 * bestand ergens anders, zoals in de Dock-app bij een adres van SharePoint,
 * dan blijft dit venster staan; vandaar de laatste zin.
 */
export function schrijfPlaatshouder(win: Window, naam?: string): void {
  try {
    win.document.title = naam ?? "Bestand openen";
    win.document.body.style.cssText =
      "font-family: system-ui, sans-serif; padding: 2rem; line-height: 1.5;";
    win.document.body.textContent = `${
      naam ? `"${naam}"` : "Het bestand"
    } wordt geopend… Opent het in een ander venster of programma, dan mag dit venster dicht.`;
  } catch {
    // Geeft de browser geen toegang tot het venster, dan blijft het leeg.
  }
}

const DESKTOP_PROTOCOLS: Record<string, string> = {
  ".docx": "ms-word:ofe|u|",
  ".doc": "ms-word:ofe|u|",
  ".xlsx": "ms-excel:ofe|u|",
  ".xls": "ms-excel:ofe|u|",
  ".pptx": "ms-powerpoint:ofe|u|",
  ".ppt": "ms-powerpoint:ofe|u|",
};

export function getDesktopAppUrl(url: string, filename: string): string | null {
  const ext = filename.slice(filename.lastIndexOf(".")).toLowerCase();
  const protocol = DESKTOP_PROTOCOLS[ext];
  if (!protocol) return null;
  return `${protocol}${url}`;
}

/** True als de extensie een Office-desktopprotocol heeft (docx/xlsx/pptx, …). */
export function isDesktopExt(filename: string): boolean {
  const ext = filename.slice(filename.lastIndexOf(".")).toLowerCase();
  return DESKTOP_PROTOCOLS[ext] !== undefined;
}

/** Archief-extensies die de browser alleen kan downloaden, niet tonen. */
const ATTACHMENT_EXTS = new Set([".zip", ".7z", ".rar", ".gz", ".tar"]);

/** True voor bestanden die als download starten i.p.v. in een tab openen. */
export function isAttachmentExt(filename: string): boolean {
  const ext = filename.slice(filename.lastIndexOf(".")).toLowerCase();
  return ATTACHMENT_EXTS.has(ext);
}

/**
 * Open een bestand waarvan we de SharePoint-`webUrl` (browser) en optioneel de
 * `webDavUrl` (desktop) al hebben — zonder extra serververzoek. Office-types met
 * een webDavUrl openen in de desktop-app (geen flitsende tab); de rest opent in
 * een browsertab.
 */
export function openWebDavOrBrowser(
  webUrl: string,
  webDavUrl: string | undefined,
  filename: string,
): void {
  const target = webDavUrl?.trim() || null;
  const desktopUrl =
    target && !opTelefoonOfTablet() ? getDesktopAppUrl(target, filename) : null;
  if (desktopUrl) {
    window.location.href = desktopUrl;
  } else {
    window.open(webUrl, "_blank", "noopener");
  }
}

/** Prefer Office desktop handlers for supported extensions; same as openDocument with filename. */
export async function openDocumentInDesktopApp(
  docId: string,
  filename: string,
) {
  return openDocument(docId, filename);
}

/** De melding van de server zelf, zonder statuscode ervoor. */
function serverTekst(err: unknown): string | null {
  const message = (err as { response?: { data?: { message?: unknown } } })
    ?.response?.data?.message;
  if (Array.isArray(message)) return message.join(", ");
  return typeof message === "string" && message ? message : null;
}

/**
 * True voor bestanden die via de OneDrive-app in een app op de eigen computer
 * te bewerken zijn (pdf, Keynote, …). Office heeft een eigen koppeling;
 * archieven en DICOM open je niet als los bestand.
 */
export function kanBewerkenInApp(naam: string): boolean {
  return !isDesktopExt(naam) && !isAttachmentExt(naam) && !isDicomFile(naam);
}

/** OneDrive van Microsoft in de Mac App Store (gecontroleerd op 28-09-2026). */
export const ONEDRIVE_MAC_APP_STORE =
  "https://apps.apple.com/nl/app/onedrive/id823766827?mt=12";

/** Een iPad meldt zich ook als Mac, maar heeft een aanraakscherm. */
export function opEenMac(): boolean {
  return (
    /Macintosh/.test(navigator.userAgent) && !(navigator.maxTouchPoints > 1)
  );
}

/**
 * Telefoon of tablet (D8): daar start de webapp nooit een app op de computer.
 * Op de user agent en niet op het aanraakscherm alleen: een Windows-laptop
 * met aanraakscherm heeft Word op de computer en houdt dat.
 */
export function opTelefoonOfTablet(): boolean {
  const ua = navigator.userAgent;
  if (/Android|iPhone|iPad|iPod|Mobile/i.test(ua)) return true;
  // iPadOS meldt zich als Mac; het aanraakscherm verraadt hem (zie opEenMac).
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
}

export type OpenRoute = "office-app" | "browser" | "inline" | "download";

/**
 * Hoe "Openen" een bestand opent (D3, D8, I18). Een pdf altijd in de browser
 * via het eigen adres (geen SharePoint-recht nodig); Office op de computer,
 * op een telefoon in de browser; op een gearchiveerd dossier waar de
 * medewerker niets mag wijzigen alleen downloaden. De Mac-route via OneDrive
 * kiest DocumentenSection vóór deze functie (opentInAppBijKlik).
 */
export function kiesOpenRoute(
  naam: string,
  opties: { telefoonOfTablet: boolean; alleenLezen: boolean },
): OpenRoute {
  if (isAttachmentExt(naam)) return "download";
  if (/\.pdf$/i.test(naam)) return "inline";
  if (opties.alleenLezen) return "download";
  if (isDesktopExt(naam)) return opties.telefoonOfTablet ? "browser" : "office-app";
  return "browser";
}

/**
 * True als een klik het bestand op een Mac in de app op de computer opent,
 * via OneDrive: Keynote altijd (I9), een pdf alleen bij de keuze ONEDRIVE (D9).
 */
export function opentInAppBijKlik(
  naam: string,
  pdfOpenenVia: PdfOpenenVia = PdfOpenenVia.BROWSER,
): boolean {
  if (!opEenMac()) return false;
  if (/\.key$/i.test(naam)) return true;
  return /\.pdf$/i.test(naam) && pdfOpenenVia === PdfOpenenVia.ONEDRIVE;
}

/**
 * Start de OneDrive-app met het bestand. Een webpagina kan niet zien of de app
 * echt is gestart; `sharePointUrl` (de map in SharePoint) is daarom altijd de
 * terugval. `gestart` is false als de server geen koppeling kon maken.
 */
export async function bewerkInApp(
  docId: string,
): Promise<{ sharePointUrl: string; gestart: boolean; account?: string }> {
  let appUrl: string | null;
  let sharePointUrl: string;
  try {
    ({ appUrl, sharePointUrl } = (
      await http.get<{ appUrl: string | null; sharePointUrl: string }>(
        `/documents/${docId}/bewerk-koppeling`,
      )
    ).data);
  } catch (err: unknown) {
    throw new Error(
      serverTekst(err) ??
        `Bewerken in app is niet gelukt: ${extractServerMessage(err)}`,
      { cause: err },
    );
  }
  // Net als bij ms-word: start de app zonder weg te navigeren.
  if (appUrl) window.location.href = appUrl;
  // Het Microsoft-account waarmee OneDrive het bestand moet openen (§4B, uitleg).
  const account = appUrl
    ? new URLSearchParams(appUrl.slice(appUrl.indexOf("?") + 1)).get("userEmail") ?? undefined
    : undefined;
  return { sharePointUrl, gestart: Boolean(appUrl), account };
}

/**
 * "Toon in OneDrive" (§4B, I18): opent de dossiermap in SharePoint. Daar kiest
 * de medewerker één keer de snelkoppeling naar OneDrive; daarna staat de map
 * in Finder. Het venster opent binnen de klik, tegen de pop-upblokkering.
 */
export async function openDossiermapInSharePoint(kenmerk: string): Promise<void> {
  const win = window.open("", "_blank");
  if (win) schrijfPlaatshouder(win, "De dossiermap");
  let url: string;
  try {
    ({ url } = (
      await http.get<{ url: string }>(
        `/records/${encodeURIComponent(kenmerk)}/documents/map-link`,
      )
    ).data);
  } catch (err: unknown) {
    win?.close();
    // dossiermap_ontbreekt en dossier_in_archief als gewone zin (fixronde WP2).
    const zin = tekstVoorServercode(serverTekst(err));
    if (zin) throw new Gebruikersfout(zin, { cause: err });
    throw new Error(`De map openen is niet gelukt: ${extractServerMessage(err)}`, {
      cause: err,
    });
  }
  if (win) win.location.href = url;
  else window.open(url, "_blank", "noopener");
}

/**
 * Vervangt de inhoud van een bestaand document; SharePoint bewaart de vorige
 * versie. De eTag van de rij gaat mee, zodat een wijziging van een ander sinds
 * het laden van de lijst niet wordt overschreven.
 */
export async function uploadNieuweVersie(
  doc: DocFileRow,
  file: File,
): Promise<void> {
  if (!doc.etag) {
    throw new Gebruikersfout(
      "Dit bestand is net geüpload. Klik op Vernieuw en probeer het daarna opnieuw.",
    );
  }
  let uploadUrl: string, headers: Record<string, string>;
  try {
    ({ uploadUrl, headers } = (
      await http.post<{ uploadUrl: string; headers: Record<string, string> }>(
        `/documents/${doc.id}/nieuwe-versie`,
        {
          bestandsnaam: file.name,
          mimeType: file.type || "application/octet-stream",
          etag: doc.etag,
        },
      )
    ).data);
  } catch (err: unknown) {
    throw new Error(
      serverTekst(err) ??
        `Nieuwe versie uploaden is niet gelukt: ${extractServerMessage(err)}`,
      { cause: err },
    );
  }

  try {
    await uploadViaSession(uploadUrl, headers, file);
  } catch (err: unknown) {
    const status = /^Storage upload failed: (\d{3})\b/.exec(
      (err as Error)?.message ?? "",
    )?.[1];
    if (status === "423") {
      throw new Gebruikersfout(
        "Het bestand is in gebruik. Sluit het bestand overal en probeer het opnieuw.",
        { cause: err },
      );
    }
    if (status === "412" || status === "409") {
      throw new Gebruikersfout(
        "Dit bestand is intussen gewijzigd. Ververs de lijst en bekijk de nieuwste versie voordat je vervangt.",
        { cause: err },
      );
    }
    throw err;
  }
}

/**
 * Zet een bestand dat in de webapp is gemaakt als nieuw document in een map
 * van het dossier (pdf-editor, "Selectie als nieuwe pdf", 26-10-2026). Dezelfde
 * drie stappen als een upload, zonder de wachtrij: het is één klein bestand. De
 * server zet het dossierprefix voor de naam en kiest bij een bezette naam "(1)".
 */
export async function uploadNieuwDocument(
  dossierKenmerk: string,
  file: File,
  folderPath?: string,
): Promise<{ id: string }> {
  const mimeType = file.type || "application/octet-stream";
  const antwoord = await vraagUploadUrl(dossierKenmerk, {
    filename: file.name,
    mimeType,
    size: file.size,
    ...(folderPath ? { folderPath } : {}),
  });
  if (antwoord.bestaatAl) {
    throw new Gebruikersfout("Deze pdf staat al in het dossier.");
  }
  const itemId = await uploadViaSession(
    antwoord.uploadUrl,
    antwoord.headers,
    file,
  );
  return legUploadVast(dossierKenmerk, {
    blobName: antwoord.blobName,
    originalFilename: file.name,
    mimeType,
    size: file.size,
    ...(itemId ? { itemId } : {}),
  });
}

/**
 * Downloads lopen via het eigen adres van de webapp, met een token (I11,
 * 29-09-2026). Een link naar het adres van de API leunt op de cookie; draait
 * de webapp als app in het Dock, dan opent zo'n adres in de standaardbrowser,
 * waar de medewerker niet is ingelogd (401). Op het eigen adres blijft de
 * download in de webapp en telt de bestandsnaam uit `download`.
 *
 * De route op het eigen adres (nginx, /api) is nieuw voor gewone downloads.
 * Daarom eerst meten: zonder token hoort de downloadroute 401 te geven. Zo
 * niet, dan loopt de download via het adres van de API, zoals voorheen.
 */
async function meetEigenAdres(): Promise<boolean> {
  try {
    const antwoord = await fetch(
      `${window.location.origin}/api/documents/file-dl`,
      { credentials: "omit" },
    );
    if (antwoord.status !== 401) return false;
    const { message } = (await antwoord.json()) as { message?: unknown };
    return message === "download_token_missing";
  } catch {
    return false;
  }
}

/**
 * De meting één keer per tabblad (19-10-2026), niet bij elke download een
 * extra verzoek en een 401 in het log. Werkt het eigen adres niet, dan meet de
 * volgende download opnieuw.
 */
let metingEigenAdres: Promise<boolean> | null = null;

export async function downloadKanViaEigenAdres(): Promise<boolean> {
  if (!metingEigenAdres) metingEigenAdres = meetEigenAdres();
  const meting = metingEigenAdres;
  const werkt = await meting;
  if (!werkt && metingEigenAdres === meting) metingEigenAdres = null;
  return werkt;
}

export function startDownload(href: string, bestandsnaam: string): void {
  const a = document.createElement("a");
  a.href = href;
  a.download = bestandsnaam;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** De map-zip op het adres van de API; de cookie gaat mee. Terugval, zie hierboven. */
export function getFolderZipUrl(
  dossierKenmerk: string,
  folderPath: string,
): string {
  const base = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");
  const params = new URLSearchParams({ folder: folderPath });
  return `${base}/records/${encodeURIComponent(
    dossierKenmerk,
  )}/documents/folder-zip?${params.toString()}`;
}

/** Wat de server over de zip zegt vóór de download. */
export interface FolderZipInfo {
  bestandsnaam: string;
  aantalBestanden: number;
  totaalBytes: number;
}

/** De map is groter dan de zip-grens; `mapUrl` wijst naar de map in SharePoint. */
export class MapTeGrootFout extends Gebruikersfout {
  mapUrl?: string;
  constructor(message: string, mapUrl?: string) {
    super(message);
    this.name = "MapTeGrootFout";
    this.mapUrl = mapUrl;
  }
}

/**
 * Download een hele map als ZIP. Eerst het token ophalen: de server
 * controleert dan de toegang en de 2 GiB-grens en geeft naam, aantal en
 * totaal terug, zodat een weigering een nette melding wordt — een 413 op een
 * `<a>`-navigatie zou het tabblad naar de JSON-foutpagina sturen. Daarna een
 * tijdelijke `<a download>`: de browser streamt rechtstreeks naar schijf.
 * Bedoeld om een Beeldvorming-/DICOM-map in één keer op te halen en lokaal in
 * Horos/OsiriX te openen.
 */
export async function downloadFolderZip(
  dossierKenmerk: string,
  folderPath: string,
): Promise<FolderZipInfo> {
  let info: FolderZipInfo & { token: string };
  try {
    ({ data: info } = await http.post<FolderZipInfo & { token: string }>(
      `/records/${encodeURIComponent(dossierKenmerk)}/documents/folder-zip-token`,
      { folder: folderPath },
    ));
  } catch (err: unknown) {
    const res = (
      err as {
        response?: {
          status?: number;
          data?: { message?: unknown; mapUrl?: unknown };
        };
      }
    )?.response;
    if (res?.status === 413) {
      throw new MapTeGrootFout(
        typeof res.data?.message === "string"
          ? res.data.message
          : "Map te groot om te zippen.",
        typeof res.data?.mapUrl === "string" ? res.data.mapUrl : undefined,
      );
    }
    if (res?.status === 404 && res.data?.message === "folder_empty") {
      throw new Gebruikersfout("Deze map bevat geen bestanden.", {
        cause: err,
      });
    }
    throw new Error(`Map-download mislukt: ${extractServerMessage(err)}`, {
      cause: err,
    });
  }
  startDownload(
    (await downloadKanViaEigenAdres())
      ? `${window.location.origin}/api/records/folder-zip-dl?token=${encodeURIComponent(info.token)}`
      : getFolderZipUrl(dossierKenmerk, folderPath),
    info.bestandsnaam,
  );
  return info;
}

/** Eén document op het adres van de API; de cookie gaat mee. Terugval, zie hierboven. */
export function getDocumentFileUrl(docId: string): string {
  const base = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");
  return `${base}/documents/${encodeURIComponent(docId)}/file`;
}

/**
 * Mint een kortlevend downloadtoken (5 min, docId-gebonden) voor een download
 * via het eigen adres: daar komt de cookie niet mee.
 */
export async function mintDownloadToken(
  docId: string,
  doel?: "openen",
): Promise<string> {
  // doel=openen: de server legt "document geopend" vast (fixronde WP2). Het
  // token voor slepen of een mailbijlage vraagt geen doel en telt niet.
  const pad = `/documents/${encodeURIComponent(docId)}/file-token`;
  const res = await http.post<{ token: string }>(
    doel ? `${pad}?doel=${doel}` : pad,
  );
  return res.data.token;
}

/**
 * Download-URL op het eigen adres (/api-proxypad i.p.v. REACT_APP_API_URL);
 * het token vervangt de cookie-auth. Voor slepen MOET het zo: Chromium
 * blokkeert cross-origin DownloadURL-drags.
 */
export function getDocumentDragUrl(token: string): string {
  return `${window.location.origin}/api/documents/file-dl?token=${encodeURIComponent(token)}`;
}

/**
 * Download één document als bestand via een tijdelijke `<a download>` naar de
 * eigen downloadroute van de webapp (hotfix 17-10-2026, zoals vóór I18). Eerst
 * het token, met doel=openen zodat "document geopend" in het activiteitenlog
 * komt: is de sessie verlopen of mag het niet, dan komt er een melding in de
 * webapp in plaats van een foutpagina van de server.
 */
export async function downloadDocumentFile(
  docId: string,
  naam?: string,
): Promise<void> {
  let token: string;
  try {
    token = await mintDownloadToken(docId, "openen");
  } catch (err: unknown) {
    throw new Error(`Downloaden is niet gelukt: ${extractServerMessage(err)}`, {
      cause: err,
    });
  }
  startDownload(
    (await downloadKanViaEigenAdres())
      ? getDocumentDragUrl(token)
      : getDocumentFileUrl(docId),
    naam || "document",
  );
}

/** Haal de bestandsinhoud op als Blob (voor o.a. e-mailbijlagen via Graph). */
export async function fetchDocumentBlob(docId: string): Promise<Blob> {
  const res = await http.get<Blob>(`/documents/${docId}/file`, {
    responseType: "blob",
  });
  return res.data;
}

/** True voor Beeldvorming-/radiologie-mappen (DICOM-studies). */
export function isImagingFolder(name: string): boolean {
  const n = (name || "").trim().toLowerCase();
  return (
    n.startsWith("beeldvorming") ||
    n.startsWith("radiolog") ||
    n.startsWith("cd ") ||
    n.includes("dicom")
  );
}

/** True voor losse DICOM-bestanden (alleen als hele map zinvol te openen). */
export function isDicomFile(naam: string): boolean {
  const n = (naam || "").trim().toLowerCase();
  return n.endsWith(".dcm") || n.endsWith(".dicom");
}

/**
 * True voor CD-zips (migratie of werkinstructie): een .zip in (een submap
 * van) Beeldvorming, of met een CD/DICOM-achtige naam zoals
 * "[Naam kenmerk] - CD 2.zip". De arts downloadt de zip, pakt hem uit en
 * opent de map in Horos/OsiriX; het radiologieverslag staat als los
 * document naast de zip.
 */
export function isImagingZip(naam: string, containingFolder?: string): boolean {
  const n = (naam || "").trim().toLowerCase();
  if (!n.endsWith(".zip")) return false;
  const folder = (containingFolder || "").trim().toLowerCase();
  if (folder === "beeldvorming" || folder.startsWith("beeldvorming/"))
    return true;
  const stem = n.slice(0, -4);
  return (
    stem.includes("dicom") ||
    stem.includes("beeldvorming") ||
    / - cd(\b|\d)/.test(stem) ||
    isImagingFolder(stem)
  );
}
